<template>
  <div class="announcement-bar">
    <Transition name="fade-slide" mode="out-in">
      <p :key="currentIndex" class="announcement-text">
        {{ currentAnnouncement }}
      </p>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import apiClient from '@/services/api'
import { useLanguage } from '@/composables/useLanguage'
import { useConfigStore } from '@/stores/configStore'

const { currentLanguage } = useLanguage()
const configStore = useConfigStore()
const announcements = ref([])
const currentIndex = ref(0)
let timer = null

const currentAnnouncement = computed(() => {
  if (announcements.value.length === 0) return ''
  return announcements.value[currentIndex.value]
})

const fetchCoupons = async () => {
  try {
    const response = await apiClient.get(`/Coupons`, {
      params: { lang: currentLanguage.value }
    })
    
    const coupons = response.data.data || []
    
    // Cerchiamo la soglia di spedizione gratuita dinamica
    const shippingCoupon = coupons.find(c => 
      c.isActive && 
      (c.description.toUpperCase().includes('SPEDIZIONE') || c.description.toUpperCase().includes('SHIPPING'))
    )

    if (shippingCoupon) {
      // 1. Proviamo a prendere il valore dal campo tecnico minimumAmount se esiste
      let threshold = shippingCoupon.minimumAmount || shippingCoupon.minimumOrderAmount || shippingCoupon.minimum_amount
      
      // 2. Fallback: Se non c'è un campo numerico, estraiamo il numero dalla descrizione (es: "sopra i 70€")
      if (!threshold) {
        const matches = shippingCoupon.description.match(/(\d+)/)
        if (matches && matches[0]) {
          threshold = parseInt(matches[0])
        }
      }

      if (threshold) {
        configStore.freeShippingThreshold = threshold
      }
    }

    const messages = coupons
      .filter(c => c.isActive)
      .map(c => c.description)
    
    announcements.value = messages
    
    // Reset index if out of bounds
    if (currentIndex.value >= announcements.value.length) {
      currentIndex.value = 0
    }
  } catch (error) {
    console.error('Error fetching coupons:', error)
    announcements.value = []
  }
}

const startTimer = () => {
  stopTimer()
  if (announcements.value.length > 1) {
    timer = setInterval(() => {
      currentIndex.value = (currentIndex.value + 1) % announcements.value.length
    }, 5000)
  }
}

const stopTimer = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

watch(currentLanguage, () => {
  fetchCoupons()
})

watch(announcements, (newVal) => {
  if (newVal.length > 1) {
    startTimer()
  } else {
    stopTimer()
  }
}, { deep: true })

onMounted(async () => {
  await fetchCoupons()
})

onUnmounted(() => {
  stopTimer()
})
</script>

<style scoped>
.announcement-bar {
  background-color: var(--teal);
  height: 35px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1001;
  overflow: hidden;
}

.announcement-text {
  color: white;
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  white-space: nowrap;
}

/* Transizione fade e slide */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.5s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 1024px) {
  .announcement-text {
    font-size: 11px;
    letter-spacing: 1px;
  }
}
</style>
